import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { LogOut, Upload, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AdminUpload from '@/components/AdminUpload';
import { useNavigate } from 'react-router-dom';

const AdminDashboard = ({ token, user, onLogout }) => {
  const [location, setLocation] = useState('zlin');
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    onLogout();
  };

  const handleUploadSuccess = () => {
    // Optionally refresh or show additional success message
  };

  return (
    <div className="min-h-screen bg-[#121212]" data-testid="admin-dashboard">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="border-b border-white/10 bg-secondary/50 backdrop-blur-md sticky top-0 z-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-heading font-bold text-white mb-1">
                Admin Dashboard
              </h1>
              <p className="text-muted-foreground">
                Přihlášen jako: <span className="text-accent font-semibold">{user.email}</span>
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 rounded-full"
                data-testid="view-public-button"
              >
                <Home className="w-4 h-4 mr-2" />
                Veřejné zobrazení
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-full"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Odhlásit se
              </Button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Location Selection */}
          <div className="bg-card border border-white/10 rounded-xl p-6">
            <label className="block text-sm font-semibold text-white mb-3">
              Vyberte lokaci
            </label>
            <select
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full md:w-64 bg-secondary border border-white/20 rounded-lg px-4 py-3 text-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
              data-testid="location-select"
            >
              <option value="zlin">Zlín</option>
              <option value="praha">Praha</option>
              <option value="brno">Brno</option>
              <option value="ostrava">Ostrava</option>
            </select>
          </div>

          {/* Upload Section */}
          <div className="bg-card border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                <Upload className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-heading font-bold text-white">
                  Nahrát inventu00e1ř
                </h2>
                <p className="text-sm text-muted-foreground">
                  CSV soubor s úplným inventu00e1řem pro lokaci: <span className="text-accent font-semibold">{location}</span>
                </p>
              </div>
            </div>
            
            <AdminUpload
              location={location}
              token={token}
              onUploadSuccess={handleUploadSuccess}
            />
          </div>

          {/* Instructions */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
            <h3 className="text-lg font-heading font-semibold text-blue-400 mb-3">
              📘 Formát CSV souboru
            </h3>
            <div className="space-y-2 text-sm text-blue-300">
              <p>Váš CSV soubor musí obsahovat následující sloupce:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><code className="bg-secondary px-2 py-0.5 rounded">slot_id</code> - Pozice v automatu (např. A1, B2)</li>
                <li><code className="bg-secondary px-2 py-0.5 rounded">item_name</code> - Název produktu</li>
                <li><code className="bg-secondary px-2 py-0.5 rounded">quantity</code> - Množství na skladě</li>
                <li><code className="bg-secondary px-2 py-0.5 rounded">price</code> - Cena v Kč</li>
                <li><code className="bg-secondary px-2 py-0.5 rounded">custom_image_url</code> - (Volitelné) URL obrázku</li>
              </ul>
              <p className="mt-3 text-xs">
                🔍 Pokud není zadán custom_image_url, systém automaticky vyhledá obrázek karty z Pokémon TCG API.
              </p>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default AdminDashboard;